import type { ApiFieldDef, ApiProfile, ProfileFieldEntry } from '@/types/inventory'

/** One action button on a material row. */
export interface MaterialAction {
  key: string
  label: string
}

/** An action as Master Data Config → Actions lists it: on or off, and what it does. */
export interface ActionSwitch extends MaterialAction {
  description: string
  active: boolean
  /** A tenant's own action, which the table has no handler for. */
  custom: boolean
}

/**
 * The actions the materials table knows how to run, in their default order.
 *
 * These are defined by the code, not by the tenant: each key has a handler in
 * `MaterialsTable`'s `runAction`. So every one of them is ON unless the tenant
 * has switched it off in Master Data Config → Actions. A tenant with no actions
 * profile, or one created before an action existed, still gets the button.
 * The backend gives every tenant matching field defs (`material-actions.ts`).
 */
export const MATERIAL_ACTIONS: readonly (MaterialAction & { description: string })[] = [
  { key: 'printLabel', label: 'Print Label', description: "Prints labels for the material's in-date lots." },
  { key: 'order', label: 'Order', description: 'Receives a new lot of the material (Add lot).' },
  // Keyed `replenish` because every tenant already has that def (it was seeded
  // as "Replansh" and hidden while it duplicated Order); it is now Reorder.
  {
    key: 'replenish',
    label: 'Reorder',
    description: 'Works out whether to re-order and how much, then hands over to a purchase order. Red when stock is low.',
  },
  { key: 'stock', label: 'Stock', description: 'Opens the row to show the lots on hand.' },
  {
    key: 'connect',
    label: 'Connect',
    description: "Connects the Inventory procedure that gates the material's status.",
  },
  {
    key: 'po',
    label: 'Purchase Order',
    description: 'Raises a purchase order straight away, at the suggested quantity.',
  },
  { key: 'delete', label: 'Delete', description: 'Deletes the material, for users allowed to.' },
]

/** The name of the actions profile every tenant starts with. */
export const DEFAULT_ACTIONS_PROFILE_NAME = 'Default'

// `icons` is a display toggle the seed carries, not an action, so it is never
// listed or drawn. Its def is left alone in the tenant's Master Data Config.
const HIDDEN_ACTION_KEYS = new Set(['icons'])

/** The tenant's Default actions profile: the one marked default, else the first. */
export function actionsProfileOf(profiles: ApiProfile[]): ApiProfile | undefined {
  return profiles.find((p) => p.isDefault) ?? profiles[0]
}

/**
 * The set of actions a material shows: the actions profile it is connected to,
 * else the Default one. A connection to a set that no longer exists also reads
 * as Default (the backend nulls it on delete, but a stale list may lag).
 */
export function actionsProfileFor(
  profiles: ApiProfile[],
  material: { actionsProfileId?: string | null },
): ApiProfile | undefined {
  const own = material.actionsProfileId
    ? profiles.find((p) => p.id === material.actionsProfileId)
    : undefined
  return own ?? actionsProfileOf(profiles)
}

/**
 * Every action with its switch, in display order — what Master Data Config →
 * Actions lists.
 *
 * A built-in action is on unless the profile stores `active: false` for it, so
 * only an explicit "off" hides it: a missing profile, def or entry all read as on.
 * A tenant's own action def keeps the generic rule: its stored switch, else the
 * def's `activeByDefault`.
 *
 * Ordered by the def's `order` where there is one, so a tenant's reordering
 * holds; a built-in with no def sorts after them in its built-in order.
 */
export function actionSwitches(defs: ApiFieldDef[], profile: ApiProfile | undefined): ActionSwitch[] {
  const fields = profile?.fields ?? {}
  const defByKey = new Map(defs.map((d) => [d.key, d]))
  const builtIn = new Set(MATERIAL_ACTIONS.map((a) => a.key))

  const rows: (ActionSwitch & { order: number })[] = [
    ...MATERIAL_ACTIONS.map((a, i) => {
      const def = defByKey.get(a.key)
      return {
        key: a.key,
        // The seeded `replenish` def is labelled "Replansh"; the code's name wins for it.
        label: def && def.label !== 'Replansh' ? def.label : a.label,
        description: a.description,
        order: def?.order ?? Number.MAX_SAFE_INTEGER - MATERIAL_ACTIONS.length + i,
        active: fields[a.key]?.active !== false,
        custom: false,
      }
    }),
    ...defs
      .filter((d) => !builtIn.has(d.key) && !HIDDEN_ACTION_KEYS.has(d.key))
      .map((d) => ({
        key: d.key,
        label: d.label,
        description: d.description ?? '',
        order: d.order,
        active: fields[d.key] ? fields[d.key].active : d.activeByDefault,
        custom: true,
      })),
  ]

  return rows.sort((a, b) => a.order - b.order).map(({ order: _order, ...rest }) => rest)
}

/** The actions to show on a material row, in display order. */
export function resolveMaterialActions(
  defs: ApiFieldDef[],
  profile: ApiProfile | undefined,
): MaterialAction[] {
  return actionSwitches(defs, profile)
    .filter((a) => a.active)
    .map(({ key, label }) => ({ key, label }))
}

/**
 * The fields to save when one action is switched on or off.
 *
 * Every listed action is written out explicitly, so the saved profile says
 * exactly what the screen showed — a switch left at its default is stored as on
 * rather than staying implicit. A boolean's switch is also its value, as the
 * profile editor writes it.
 */
export function fieldsWithSwitch(
  switches: ActionSwitch[],
  profile: ApiProfile | undefined,
  key: string,
  on: boolean,
): Record<string, ProfileFieldEntry> {
  const fields: Record<string, ProfileFieldEntry> = { ...(profile?.fields ?? {}) }
  for (const s of switches) {
    const active = s.key === key ? on : s.active
    fields[s.key] = { active, value: active }
  }
  return fields
}
